import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import styles from './stage.module.scss'

/*
 * Die Bühne einer vertonten Präsentation — nach dem Muster der TalentsLounge
 * (Buehne.tsx), angepasst an dieses Repo (React 17, ohne framer-motion).
 *
 * TAKT: Es gibt keine Zeitmarken. Jede Folie hat Schritte, jeder Schritt eine
 * Aufnahme; das Ende der Aufnahme schaltet weiter (nächster Schritt, sonst
 * nächste Folie). Die Folie deckt mit `<Reveal at={n}>` auf, was der Schritt
 * sagt.
 *
 * MUSIK läuft als eigene Spur in Schleife und wird leiser, solange jemand
 * spricht (900 ms überblendet). Sie startet erst mit „Abspielen" — Browser
 * erlauben Ton nur nach einer Handlung.
 *
 * DRUCK: `?druck=1` zeigt alle Schritte auf einmal und keine Bedienleiste;
 * `#3` springt auf Folie 3. So entsteht das PDF (scripts/presentation-pdf.mjs).
 */

const StepContext = createContext(0)

/** Aktueller Schritt der Folie — im Druck `Infinity`, damit alles zu sehen ist. */
export const useStep = () => useContext(StepContext)

/** Erscheint, sobald die Folie bei Schritt `at` angekommen ist. */
export function Reveal({ at = 0, className, children, as: Tag = 'div' }) {
	const step = useStep()
	return <Tag className={clsx(styles.reveal, step >= at && styles.shown, className)}>{children}</Tag>
}

Reveal.propTypes = {
	at: PropTypes.number,
	className: PropTypes.string,
	children: PropTypes.node,
	as: PropTypes.elementType
}

const MUSIC_LOUD = 0.22
const MUSIC_SOFT = 0.08
const TEMPI = [1, 1.1, 1.2, 1.3]

/** Kurzer Prüfwert über Text und Länge: neu vertont heißt neue Adresse, kein alter Stand aus dem Zwischenspeicher. */
const fingerprint = text => {
	let h = 2166136261
	for (let i = 0; i < text.length; i++) h = Math.imul(h ^ text.charCodeAt(i), 16777619)
	return (h >>> 0).toString(36)
}

const slideFromHash = count => {
	const n = parseInt(String(window.location.hash).replace('#', ''), 10)
	return Number.isFinite(n) && n >= 1 && n <= count ? n - 1 : 0
}

export default function Stage({ slides, audioBase, music, pdf, tempo: startTempo, renderSlide }) {
	const { t } = useTranslation()
	const print = useMemo(() => new URLSearchParams(window.location.search).get('druck') === '1', [])

	const [index, setIndex] = useState(() => slideFromHash(slides.length))
	const [step, setStep] = useState(0)
	const [playing, setPlaying] = useState(false)
	const [speaking, setSpeaking] = useState(false)
	const [voiceOn, setVoiceOn] = useState(true)
	const [musicOn, setMusicOn] = useState(true)
	const [tempo, setTempo] = useState(startTempo)
	const [manifest, setManifest] = useState({})

	const frameRef = useRef(null)
	const voiceRef = useRef(null)
	const musicRef = useRef(null)

	const slide = slides[index]
	const steps = slide.steps || 1

	useEffect(() => {
		let aktiv = true
		fetch(`${audioBase}/manifest.json`, { cache: 'no-store' })
			.then(r => (r.ok ? r.json() : { lines: [] }))
			.then(data => {
				if (!aktiv) return
				const map = {}
				for (const line of data.lines || []) map[`${line.slide}#${line.step}`] = line
				setManifest(map)
			})
			.catch(() => aktiv && setManifest({}))
		return () => {
			aktiv = false
		}
	}, [audioBase])

	// Im Druck folgt die Folie der Adresse (#n), damit das Skript blättern kann.
	useEffect(() => {
		const onHash = () => {
			setIndex(slideFromHash(slides.length))
			setStep(0)
		}
		window.addEventListener('hashchange', onHash)
		return () => window.removeEventListener('hashchange', onHash)
	}, [slides.length])

	useEffect(() => {
		if (print) return
		const hash = `#${index + 1}`
		if (window.location.hash !== hash) window.history.replaceState(null, '', hash)
	}, [index, print])

	const goTo = useCallback(
		(nextIndex, nextStep = 0) => {
			if (nextIndex < 0) return
			if (nextIndex >= slides.length) {
				setPlaying(false)
				return
			}
			setIndex(nextIndex)
			setStep(nextStep)
		},
		[slides.length]
	)

	const next = useCallback(() => {
		if (step + 1 < steps) setStep(step + 1)
		else goTo(index + 1)
	}, [step, steps, index, goTo])

	const prev = useCallback(() => {
		if (step > 0) setStep(step - 1)
		else if (index > 0) goTo(index - 1, (slides[index - 1].steps || 1) - 1)
	}, [step, index, slides, goTo])

	// Die Stimme: je Schritt eine Aufnahme, ihr Ende schaltet weiter.
	useEffect(() => {
		const audio = voiceRef.current
		if (!audio || print) return undefined
		audio.pause()
		setSpeaking(false)
		if (!playing) return undefined
		const line = manifest[`${slide.id}#${step}`]
		if (!line) {
			// Ohne Aufnahme nicht stehen bleiben: kurz zeigen, dann weiter.
			const timer = setTimeout(next, 2500)
			return () => clearTimeout(timer)
		}
		const timer = setTimeout(
			() => {
				audio.src = `${audioBase}/${line.file}?v=${fingerprint(`${line.text}|${line.seconds}`)}`
				audio.playbackRate = tempo
				audio.preservesPitch = true
				audio.muted = !voiceOn
				audio.play().then(
					() => setSpeaking(true),
					() => setPlaying(false)
				)
			},
			step === 0 ? 700 : 250
		)
		return () => clearTimeout(timer)
		// `next` und `voiceOn` bewusst nicht: Ein Umschalten soll die laufende Aufnahme nicht neu starten.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [playing, index, step, manifest, audioBase, print])

	useEffect(() => {
		if (voiceRef.current) voiceRef.current.muted = !voiceOn
	}, [voiceOn])

	useEffect(() => {
		if (voiceRef.current) voiceRef.current.playbackRate = tempo
	}, [tempo])

	// Die Musik: startet mit dem Abspielen, duckt sich unter der Stimme.
	useEffect(() => {
		const audio = musicRef.current
		if (!audio || print) return
		if (playing && musicOn) {
			if (audio.paused) {
				audio.volume = 0
				audio.play().catch(() => {})
			}
		} else {
			audio.pause()
		}
	}, [playing, musicOn, print])

	useEffect(() => {
		const audio = musicRef.current
		if (!audio || print) return undefined
		const ziel = speaking ? MUSIC_SOFT : MUSIC_LOUD
		const start = audio.volume
		const begonnen = Date.now()
		const timer = setInterval(() => {
			const anteil = Math.min(1, (Date.now() - begonnen) / 900)
			audio.volume = start + (ziel - start) * anteil
			if (anteil >= 1) clearInterval(timer)
		}, 50)
		return () => clearInterval(timer)
	}, [speaking, playing, print])

	const togglePlay = useCallback(() => setPlaying(p => !p), [])

	const restart = () => {
		goTo(0)
		setPlaying(true)
		if (musicRef.current) musicRef.current.currentTime = 0
	}

	const fullscreen = () => {
		const el = frameRef.current
		if (!el) return
		if (document.fullscreenElement) document.exitFullscreen()
		else el.requestFullscreen?.()
	}

	useEffect(() => {
		if (print) return undefined
		const onKey = event => {
			if (event.target.closest?.('input, select, textarea')) return
			const key = event.key
			if (key === ' ' || key === 'k' || key === 'K') {
				event.preventDefault()
				togglePlay()
			} else if (key === 'ArrowRight' || key === 'PageDown') next()
			else if (key === 'ArrowLeft' || key === 'PageUp') prev()
			else if (key === 'm' || key === 'M') setMusicOn(v => !v)
			else if (key === 's' || key === 'S') setVoiceOn(v => !v)
			else if (key === 'f' || key === 'F') fullscreen()
		}
		window.addEventListener('keydown', onKey)
		return () => window.removeEventListener('keydown', onKey)
	}, [print, next, prev, togglePlay])

	return (
		<div className={clsx(styles.stage, print && styles.print)}>
			<div ref={frameRef} className={styles.frameWrap}>
				<div className={styles.frame} data-slide={slide.id}>
					<StepContext.Provider value={print ? Infinity : step}>
						<div key={slide.id} className={styles.slide}>
							{renderSlide(slide.id)}
						</div>
					</StepContext.Provider>
					{!print && (
						<div className={styles.dots} aria-hidden="true">
							{slides.map((s, i) => (
								<span
									key={s.id}
									className={clsx(i === index && styles.dotActive, i < index && styles.dotDone)}
								/>
							))}
						</div>
					)}
				</div>
			</div>

			{!print && (
				<>
					<div className={styles.controls}>
						<button type="button" className={styles.primary} onClick={togglePlay}>
							{playing ? t('presentationPause') : t('presentationPlay')}
						</button>
						<button type="button" onClick={prev} disabled={index === 0 && step === 0}>
							← {t('presentationPrev')}
						</button>
						<span className={styles.position}>
							{index + 1} / {slides.length} · {slide.short}
						</span>
						<button
							type="button"
							onClick={next}
							disabled={index === slides.length - 1 && step === steps - 1}
						>
							{t('presentationNext')} →
						</button>
						<button type="button" onClick={restart}>
							{t('presentationRestart')}
						</button>
						<span className={styles.spacer} />
						<label className={styles.toggle}>
							<input type="checkbox" checked={voiceOn} onChange={e => setVoiceOn(e.target.checked)} />
							{t('presentationVoice')}
						</label>
						<label className={styles.toggle}>
							<input type="checkbox" checked={musicOn} onChange={e => setMusicOn(e.target.checked)} />
							{t('presentationMusic')}
						</label>
						<label className={styles.toggle}>
							{t('presentationTempo')}
							<select value={tempo} onChange={e => setTempo(Number(e.target.value))}>
								{TEMPI.map(v => (
									<option key={v} value={v}>
										{v.toFixed(1)}×
									</option>
								))}
							</select>
						</label>
						<button type="button" onClick={fullscreen}>
							{t('presentationFullscreen')}
						</button>
						{pdf && (
							<a className={styles.pdf} href={pdf} download>
								{t('presentationPdf')}
							</a>
						)}
					</div>
					<p className={styles.keys}>{t('presentationKeys')}</p>
					<audio
						ref={voiceRef}
						onEnded={() => {
							setSpeaking(false)
							next()
						}}
						preload="auto"
					/>
					{music && <audio ref={musicRef} src={music} loop preload="auto" />}
				</>
			)}
		</div>
	)
}

Stage.propTypes = {
	slides: PropTypes.arrayOf(
		PropTypes.shape({ id: PropTypes.string, short: PropTypes.string, steps: PropTypes.number })
	).isRequired,
	audioBase: PropTypes.string.isRequired,
	music: PropTypes.string,
	pdf: PropTypes.string,
	tempo: PropTypes.number,
	renderSlide: PropTypes.func.isRequired
}

Stage.defaultProps = {
	music: null,
	pdf: null,
	tempo: 1
}
